import { useCallback, useEffect, useRef, useState } from "react";

import { getItemsByIds, getItemsForGroup } from "@/services/monday-service";
import type { BoardGroup, Group, Item } from "@/types/common";

const LIMIT = 100;
export interface Groups {
  [key: string]: Group;
}

const chunk = (arr: string[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));

export function useInfiniteScroll(loadNextPage: () => void) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const handleObserver = useCallback(
    ([target]: IntersectionObserverEntry[]) => {
      if (target.isIntersecting) {
        loadNextPage();
      }
    },
    [loadNextPage]
  );
  const option = {
    root: null,
    rootMargin: "0px 0px 200px 0px",
    threshold: 0,
  };

  const observer = useRef(new IntersectionObserver(handleObserver, option));

  useEffect(() => {
    const currentObserver = observer.current;
    if (element) {
      currentObserver.observe(element);
    }

    return () => {
      if (element) {
        currentObserver.unobserve(element);
      }
    };
  }, [handleObserver, element]);

  return { setElement };
}

export function useQueryItemsForGroups(boardId: number, groups: BoardGroup[]) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [grouped, setGrouped] = useState<Groups>({});
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const [cursor, setCursor] = useState<{
    groupIndex: number;
    currentCursor: string | null;
  }>({
    groupIndex: 0,
    currentCursor: null,
  });

  const [fetchLimit, setFetchLimit] = useState(LIMIT);

  const loadNextPage = useCallback(() => {
    setFetchLimit((prev) => prev + LIMIT);
  }, []);

  useEffect(() => {
    void (async () => {
      if (groups.length > 0) {
        try {
          if (totalItems > fetchLimit) {
            return;
          }
          setLoading(true);
          setError(false);
          const id = groups[cursor.groupIndex].id;

          const { items: res, cursor: newCursor } = await getItemsForGroup(boardId, id, LIMIT, cursor.currentCursor);
          setTotalItems((prev) => prev + res.length);

          setGrouped((prev) => {
            if (!prev[id]) {
              return { ...prev, [id]: { ...groups[cursor.groupIndex], items: res } };
            } else {
              return { ...prev, [id]: { ...groups[cursor.groupIndex], items: [...prev[id].items, ...res] } };
            }
          });

          if (newCursor) {
            setCursor((prev) => ({ ...prev, currentCursor: newCursor }));
          } else if (groups[cursor.groupIndex + 1]) {
            setCursor((prev) => ({ currentCursor: null, groupIndex: prev.groupIndex + 1 }));
          } else {
            setHasMore(false);
          }
        } catch (err) {
          setError(true);
        } finally {
          setLoading(false);
        }
      }
    })()
      .then(() => {})
      .catch(() => {});
  }, [fetchLimit, JSON.stringify(cursor), boardId, JSON.stringify(groups)]);

  return { loading, error, grouped, hasMore, loadNextPage };
}

export function useQueryItemsByIds(ids: string[]) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const chunks = chunk(ids, LIMIT);

  const loadNextPage = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const batch = chunks[page - 1];
        if (!batch) {
          setHasMore(false);
        }
        const res = await getItemsByIds(batch, LIMIT);
        setItems((prev) => sortedItems([...prev, ...res]));
      } finally {
        setLoading(false);
      }
    })()
      .then(() => {})
      .catch(() => {});
  }, [JSON.stringify(chunks), page]);

  useEffect(() => {
    setItems([]);
    setHasMore(true);
    setPage(1);
  }, [JSON.stringify(ids)]);

  return { loading, items, hasMore, loadNextPage };
}

const sortedItems = (items: Item[]) =>
  Object.values(
    items.reduce<Groups>((groups: Groups, item: Item) => {
      const id = item.group.id;
      if (!groups[id]) {
        return { ...groups, [id]: { ...item.group, items: [item] } };
      } else {
        groups[id].items.push(item);
        return groups;
      }
    }, {})
  )
    .sort(({ position: p1 }, { position: p2 }) => (Number(p1) > Number(p2) ? 1 : -1))
    .map((group) => group.items)
    .flat();
